import { FiscalNumber } from "src/app/ui/models/domains/pbm/fiscal-number";
import { StateAcronym } from "src/app/ui/models/domains/pbm/state/state-acronym";
import { MedicalPrescription } from "src/app/ui/models/domains/pbm/transactional-management/medical-prescription";
import { MedicalPrescriptionDocumentKey } from "src/app/ui/models/domains/pbm/transactional-management/medical-prescription-document-key";
import { PaymentMethod } from "src/app/ui/models/domains/pbm/transactional-management/payment-method";
import { PractitionerRegistration } from "src/app/ui/models/domains/pbm/transactional-management/practitioner-registration";
import { ProductItem } from "src/app/ui/models/domains/pbm/transactional-management/product-item";
import { TransactionalManagementIndicators } from "src/app/ui/models/domains/pbm/transactional-management/transactional-management-indicators";
import { TransactionalManagementSale } from "src/app/ui/models/domains/pbm/transactional-management/transactional-management-sale";
import { TransactionalManagementResult } from "src/app/ui/models/domains/pbm/transactional-management/transactional-management.result";

export function parseIndicators(payload: any): TransactionalManagementIndicators {
  const indicators = new TransactionalManagementIndicators();
  payload.map((item) => {
    switch (item.SalesOrderMonitorStatusDescription) {
      case "Open":
        indicators.setOpenIndicator(item.TotalCount);
        break;
      case "Pending":
        indicators.setPendingIndicator(item.TotalCount);
        break;
      case "Confirmed":
        indicators.setConfirmedIndicator(item.TotalCount);
        break;
      case "Cancelled":
        indicators.setCancelledIndicator(item.TotalCount);
        break;
      case "Expired":
        indicators.setExpiredIndicator(item.TotalCount);
        break;
      case "Rejected":
        indicators.setRejectedIndicator(item.TotalCount);
        break;
      case "Refund":
        indicators.setRefundIndicator(item.TotalCount);
        break;
      case "Refunded":
        indicators.setRefundedIndicator(item.TotalCount);
        break;
      case "Inconsistent":
        indicators.setInconsistentIndicator(item.TotalCount);
        break;
      case "Exceeded":
        indicators.setExceededIndicator(item.TotalCount);
        break;
      default:
        break;
    }
  });
  return indicators;
}

export function parseTransactionalManagementResult(payload: any): TransactionalManagementResult {
  const result = new TransactionalManagementResult();
  result.setStart(payload.Start);
  result.setTotal(payload.Total);
  result.setTotalPerPages(payload.TotalPerPages);
  result.setSales(parseSales(payload.Data));
  return result;
}

export function parseSale(payload: any): TransactionalManagementSale {
  const sale = new TransactionalManagementSale();
  sale.setId(payload.Id);
  sale.setUniqueSequencialNumber(payload.UniqueSequentialNumber);
  sale.setType(payload.OrderStatus);
  sale.setBeneficiarySocialNumber(payload.Beneficiary.SocialNumber);
  sale.setBeneficiaryName(payload.Beneficiary.Name);
  sale.setIsHolder(payload.Beneficiary.IsHolder);
  sale.setCardNumber(payload.Beneficiary.Card.CardNumber);
  sale.setNetworkFiscalNumber(new FiscalNumber(payload.NetworkInfo.FiscalNumber));
  sale.setTotalPrice(payload.TotalPrice);
  sale.setTotalCardValue(payload.TotalCardValue);
  sale.setTotalCheckoutValue(payload.TotalCheckoutValue);
  sale.setTransactionDate(payload.TransactionDate);
  sale.setMonitorStatus(payload.SalesOrderMonitorStatus);
  if (payload.Items)
    sale.setProductItems(parseProductItems(payload.Items));
  if (payload.UniqueSequentialNumberCorrelated)
    sale.setUniqueSequencialNumberCorrelated(payload.UniqueSequentialNumberCorrelated.Value);
  return sale;
}

export function parseSales(payload: any): TransactionalManagementSale[] {
  const sales: TransactionalManagementSale[] = [];
  payload.map((item) => {
    const sale = new TransactionalManagementSale();
    sale.setId(item.Id);
    sale.setUniqueSequencialNumber(item.UniqueSequentialNumber);
    sale.setType(item.OrderStatus);
    sale.setBeneficiarySocialNumber(item.Beneficiary.SocialNumber);
    sale.setBeneficiaryName(item.Beneficiary.Name);
    sale.setIsHolder(item.Beneficiary.IsHolder);
    sale.setCardNumber(item.Beneficiary.Card.CardNumber);
    sale.setNetworkFiscalNumber(new FiscalNumber(item.NetworkInfo.FiscalNumber));
    sale.setTotalPrice(item.TotalPrice);
    sale.setTotalCardValue(item.TotalCardValue);
    sale.setTotalCheckoutValue(item.TotalCheckoutValue);
    sale.setTransactionDate(item.TransactionDate);
    sale.setShippingType(item.ShippingType);
    sale.setMonitorStatus(item.SalesOrderMonitorStatus);
    if (item.Items)
      sale.setProductItems(parseProductItems(item.Items));
    if (item.UniqueSequentialNumberCorrelated)
      sale.setUniqueSequencialNumberCorrelated(item.UniqueSequentialNumberCorrelated);

    sales.push(sale);
  });
  return sales;
}

export function parseProductItems(payload: any): ProductItem[] {
  const productItems: ProductItem[] = [];
  payload.map((item) => {
    const productItem = new ProductItem();
    productItem.setEan(item.EAN);
    productItem.setProductName(item.ProductName);
    productItem.setGrossPrice(item.GrossPrice);
    productItem.setPrice(item.Price);
    productItem.setDiscountValue(item.DiscountValue);
    productItem.setSubsidyValue(item.SubsidyValue);
    productItem.setEmploymentPayrollValue(item.EmploymentPayrollValue);
    productItem.setCheckoutValue(item.CheckoutValue);
    productItem.setCardValue(item.CardValue);
    productItem.setBeneficiaryValue(item.BeneficiaryValue);
    productItem.setAuthorizedQuantity(item.AuthorizedQuantity);
    if (item.Messages)
      productItem.setErrorMessage(item.Messages.length > 0 ? item.Messages[0].Message : "");
    productItem.setMedicalPrescription(item.MedicalPrescription ? parseMedicalPrescription(item.MedicalPrescription) : null);
    productItem.setUsedNetworkNetPrice(item.UsedNetworkNetPrice);
    productItem.setUsedNetworkGrossPrice(item.UsedNetworkGrossPrice);
    productItem.setMedicalPrescriptionStatus(item.MedicalPrescriptionStatus);

    productItems.push(productItem);
  });
  return productItems;
}

function parseMedicalPrescription(payload: any): MedicalPrescription {
  const medicalPrescription = new MedicalPrescription();
  medicalPrescription.setMedicalPrescriptionDate(payload.MedicalPrescriptionDate);
  medicalPrescription.setMedicalPrescriptionInsertionDate(payload.MedicalPrescriptionDate);
  medicalPrescription.setPractitionerRegistration(parsePractitionerRegistration(payload));
  medicalPrescription.setQuantity(payload.Quantity);
  return medicalPrescription;
}

function parsePractitionerRegistration(payload: any): PractitionerRegistration {
  const registration = new PractitionerRegistration();
  registration.setRegistration(payload.PractitionerRegistration);
  registration.setState(new StateAcronym(payload.PractitionerState));
  return registration;
}
